import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Coins, RefreshCw, Plus, X, Loader2, Trash2, ShoppingBag, Crown, Terminal, Settings2, Wallet, Server, Tag } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` } });

const inputCls = 'w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-3 py-2.5 text-[var(--text-primary)] text-sm font-[Manrope] placeholder-[var(--text-muted)] focus:outline-none focus:border-[#5865F2]/50';
const labelCls = 'text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5';
const cardCls = 'bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5';

const DEFAULT_SETTINGS = { currency_name: 'coins', currency_emoji: '🪙', daily_amount: 100, work_min: 20, work_max: 80, levelup_bonus: 50 };
const MEDALS = ['🥇', '🥈', '🥉'];
const COMMANDS = [
  ['/balance', 'Check your wallet'],
  ['/daily', 'Claim your daily reward'],
  ['/work', 'Earn a random amount'],
  ['/pay', 'Send currency to a member'],
  ['/shop', 'Browse the server shop'],
  ['/buy', 'Buy an item from the shop'],
  ['/richest', 'Show the richest members'],
  ['/rank', 'Show your level and XP'],
  ['/leaderboard', 'Show the XP leaderboard'],
];

const fmtNum = (n) => Number(n || 0).toLocaleString();

function AddItemModal({ guildId, roles, emoji, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState(100);
  const [roleId, setRoleId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const valid = name.trim() && Number(price) > 0;

  const submit = async () => {
    if (!valid) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/api/economy/${guildId}/shop`, {
        name: name.trim(),
        description: description.trim(),
        price: Math.floor(Number(price)),
        role_id: roleId || null,
      }, authHeader());
      onCreated(data);
      toast.success('Item added to shop');
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add item.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-2xl p-6 w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] text-lg flex items-center gap-2">
            <ShoppingBag size={18} className="text-[#5865F2]" /> Add Shop Item
          </h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} maxLength={100} placeholder="e.g. VIP Role" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={300} rows={2} placeholder="What does this item give?" className={`${inputCls} resize-y`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Price {emoji}</label>
              <input type="number" min={1} value={price} onChange={e => setPrice(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Role (optional)</label>
              <select value={roleId} onChange={e => setRoleId(e.target.value)} className={inputCls}>
                <option value="">None</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {error && <p className="text-red-400 text-xs mt-3">{error}</p>}

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-sm font-[Manrope] transition-colors">Cancel</button>
          <button
            onClick={submit}
            disabled={!valid || loading}
            className="flex-1 py-2.5 rounded-xl bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            Add Item
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function SettingsCard({ guildId, settings, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    const body = {
      currency_name: String(form.currency_name || '').trim() || DEFAULT_SETTINGS.currency_name,
      currency_emoji: String(form.currency_emoji || '').trim() || DEFAULT_SETTINGS.currency_emoji,
      daily_amount: Math.max(0, Math.floor(Number(form.daily_amount) || 0)),
      work_min: Math.max(0, Math.floor(Number(form.work_min) || 0)),
      work_max: Math.max(0, Math.floor(Number(form.work_max) || 0)),
      levelup_bonus: Math.max(0, Math.floor(Number(form.levelup_bonus) || 0)),
    };
    if (body.work_max < body.work_min) {
      toast.error('Work max must be at least work min');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/api/economy/${guildId}/settings`, body, authHeader());
      toast.success('Economy settings saved');
      onSaved(body);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const num = (k, label) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input type="number" min={0} value={form[k] ?? ''} onChange={e => set(k, e.target.value)} className={inputCls} />
    </div>
  );

  return (
    <div className={cardCls}>
      <h2 className="flex items-center gap-2 text-[var(--text-primary)] font-semibold font-[Outfit] mb-4">
        <Settings2 size={16} className="text-[#5865F2]" /> Economy settings
      </h2>
      <div className="space-y-3">
        <div className="grid grid-cols-[1fr_90px] gap-3">
          <div>
            <label className={labelCls}>Currency name</label>
            <input type="text" maxLength={32} value={form.currency_name ?? ''} onChange={e => set('currency_name', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Emoji</label>
            <input type="text" maxLength={64} value={form.currency_emoji ?? ''} onChange={e => set('currency_emoji', e.target.value)} className={`${inputCls} text-center`} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {num('daily_amount', 'Daily reward')}
          {num('levelup_bonus', 'Level-up bonus / lvl')}
          {num('work_min', 'Work min')}
          {num('work_max', 'Work max')}
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40"
        >
          {saving && <Loader2 size={14} className="animate-spin" />} Save settings
        </button>
      </div>
    </div>
  );
}

function AdjustCard({ guildId, leaderboard, emoji, onAdjusted }) {
  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (sign) => {
    const uid = userId.trim();
    const amt = Math.floor(Math.abs(Number(amount)));
    if (!/^\d{15,22}$/.test(uid)) { toast.error('Enter a valid Discord user ID'); return; }
    if (!amt) { toast.error('Enter an amount'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/economy/${guildId}/adjust`, { user_id: uid, amount: sign * amt }, authHeader());
      toast.success(`New balance: ${emoji} ${fmtNum(data.balance)}`);
      setAmount('');
      onAdjusted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to adjust balance');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cardCls}>
      <h2 className="flex items-center gap-2 text-[var(--text-primary)] font-semibold font-[Outfit] mb-4">
        <Wallet size={16} className="text-[#5865F2]" /> Adjust balance
      </h2>
      <div className="space-y-3">
        {leaderboard.length > 0 && (
          <div>
            <label className={labelCls}>Pick a member</label>
            <select value={leaderboard.some(u => String(u.user_id) === userId) ? userId : ''} onChange={e => setUserId(e.target.value)} className={inputCls}>
              <option value="">— Choose from leaderboard —</option>
              {leaderboard.map(u => <option key={u.user_id} value={String(u.user_id)}>{u.username || u.user_id}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className={labelCls}>User ID</label>
          <input type="text" inputMode="numeric" value={userId} onChange={e => setUserId(e.target.value.trim())} placeholder="Paste a Discord user ID" className={`${inputCls} font-mono`} />
        </div>
        <div>
          <label className={labelCls}>Amount {emoji}</label>
          <input type="number" min={1} value={amount} onChange={e => setAmount(e.target.value)} placeholder="100" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => submit(1)}
            disabled={busy}
            className="py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40"
          >
            + Add
          </button>
          <button
            onClick={() => submit(-1)}
            disabled={busy}
            className="py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40"
          >
            − Remove
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Economy() {
  const { selectedGuildId, selectedGuild } = useGuild();
  const isStaff = !!selectedGuild?.is_staff;
  const isGlobal = !!selectedGuild?.is_global;
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [leaderboard, setLeaderboard] = useState([]);
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!selectedGuildId || isGlobal) { setLoading(false); return; }
    const [s, lb, shop] = await Promise.allSettled([
      axios.get(`${API}/api/economy/${selectedGuildId}/settings`, authHeader()),
      axios.get(`${API}/api/economy/${selectedGuildId}/leaderboard`, authHeader()),
      axios.get(`${API}/api/economy/${selectedGuildId}/shop`, authHeader()),
    ]);
    if (s.status === 'fulfilled') setSettings({ ...DEFAULT_SETTINGS, ...(s.value.data || {}) });
    if (lb.status === 'fulfilled') setLeaderboard(lb.value.data.leaderboard || []);
    if (shop.status === 'fulfilled') setItems(shop.value.data.items || []);
    setError(lb.status === 'rejected' && shop.status === 'rejected' ? 'Failed to load economy data.' : null);
    setLoading(false);
  }, [selectedGuildId, isGlobal]);

  const fetchLeaderboard = async () => {
    try {
      const r = await axios.get(`${API}/api/economy/${selectedGuildId}/leaderboard`, authHeader());
      setLeaderboard(r.data.leaderboard || []);
    } catch {}
  };

  useEffect(() => {
    setLoading(true);
    fetchAll();
  }, [selectedGuildId, isGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isStaff || isGlobal || !selectedGuildId) { setRoles([]); return; }
    axios.get(`${API}/api/reaction-roles/${selectedGuildId}/roles`, authHeader())
      .then(r => setRoles(r.data.roles || []))
      .catch(() => setRoles([]));
  }, [selectedGuildId, isStaff, isGlobal]);

  const deleteItem = async (id) => {
    if (!window.confirm('Remove this item from the shop?')) return;
    try {
      await axios.delete(`${API}/api/economy/${selectedGuildId}/shop/${id}`, authHeader());
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Item removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove item');
    }
  };

  const emoji = settings.currency_emoji || DEFAULT_SETTINGS.currency_emoji;

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="economy-main">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-[Outfit] text-[var(--text-primary)] flex items-center gap-3">
              {isGlobal ? <Coins size={28} className="text-[#5865F2]" /> : <span className="text-3xl leading-none">{emoji}</span>}
              Economy
            </h1>
            <p className="text-[var(--text-muted)] text-sm mt-1 font-[Manrope]">
              {isGlobal ? 'Server currency, shop and leaderboard' : `Earn, spend and trade ${settings.currency_name || 'coins'}`}
            </p>
          </div>
          {!isGlobal && (
            <button
              onClick={fetchAll}
              className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-sm font-[Manrope] px-3 py-2 rounded-lg hover:bg-[var(--bg-elevated)] flex-shrink-0"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          )}
        </div>

        {isGlobal ? (
          <div className="max-w-3xl bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
            <Server size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
            <p className="text-[var(--text-muted)] font-[Manrope] text-sm">Select a specific server to view its economy</p>
          </div>
        ) : (
          <div className="max-w-6xl space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm font-[Manrope]">{error}</div>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Leaderboard */}
              <section className={cardCls}>
                <h2 className="flex items-center gap-2 text-[var(--text-primary)] font-semibold font-[Outfit] mb-4">
                  <Crown size={16} className="text-amber-400" /> Richest members
                </h2>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8">
                    <Wallet size={28} className="text-[var(--text-muted)] mx-auto mb-2" />
                    <p className="text-[var(--text-muted)] font-[Manrope] text-sm">Nobody has earned anything yet</p>
                    <p className="text-[var(--text-muted)] font-[Manrope] text-xs mt-1">Try /daily in Discord</p>
                  </div>
                ) : (
                  <ol className="space-y-1">
                    {leaderboard.map((u, i) => (
                      <li key={u.user_id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors">
                        <span className="w-7 text-center flex-shrink-0">
                          {i < 3 ? <span className="text-lg">{MEDALS[i]}</span> : <span className="text-[var(--text-muted)] text-sm font-mono">{i + 1}</span>}
                        </span>
                        <span className="flex-1 min-w-0 text-[var(--text-primary)] text-sm font-medium font-[Manrope] truncate">{u.username || u.user_id}</span>
                        <span className="text-[var(--text-secondary)] text-sm font-semibold font-[Manrope] flex-shrink-0">{emoji} {fmtNum(u.balance)}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </section>

              {/* Shop */}
              <section className={cardCls}>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="flex items-center gap-2 text-[var(--text-primary)] font-semibold font-[Outfit]">
                    <ShoppingBag size={16} className="text-[#5865F2]" /> Shop
                  </h2>
                  {isStaff && (
                    <button
                      onClick={() => setShowAdd(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-xs font-medium font-[Manrope] transition-colors"
                    >
                      <Plus size={13} /> Add item
                    </button>
                  )}
                </div>
                {items.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingBag size={28} className="text-[var(--text-muted)] mx-auto mb-2" />
                    <p className="text-[var(--text-muted)] font-[Manrope] text-sm">The shop is empty</p>
                    {isStaff && <p className="text-[var(--text-muted)] font-[Manrope] text-xs mt-1">Add an item to give members something to spend on</p>}
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <AnimatePresence mode="popLayout">
                      {items.map(item => (
                        <motion.div
                          key={item.id}
                          layout
                          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                          className="bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-4 flex flex-col gap-2"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-[var(--text-primary)] font-semibold font-[Outfit] text-sm break-words min-w-0">{item.name}</p>
                            {isStaff && (
                              <button
                                onClick={() => deleteItem(item.id)}
                                className="flex-shrink-0 text-[var(--text-muted)] hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-red-500/10"
                                title="Remove item"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-[var(--text-muted)] text-xs font-[Manrope] whitespace-pre-wrap break-words">{item.description}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 mt-auto pt-1">
                            <span className="text-amber-400 text-sm font-semibold font-[Manrope]">{emoji} {fmtNum(item.price)}</span>
                            {item.role_id && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-[Manrope] px-2 py-0.5 rounded-full bg-[#5865F2]/10 border border-[#5865F2]/20 text-[#5865F2]">
                                <Tag size={10} /> {item.role_name || 'Role'}
                              </span>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </section>
            </div>

            <div className={`grid gap-6 ${isStaff ? 'lg:grid-cols-3' : ''}`}>
              {/* Commands */}
              <section className={cardCls}>
                <h2 className="flex items-center gap-2 text-[var(--text-primary)] font-semibold font-[Outfit] mb-4">
                  <Terminal size={16} className="text-[#5865F2]" /> Discord commands
                </h2>
                <ul className="space-y-2">
                  {COMMANDS.map(([cmd, desc]) => (
                    <li key={cmd} className="flex items-baseline gap-3 text-sm font-[Manrope]">
                      <code className="px-1.5 py-0.5 rounded bg-[var(--bg-hover)] text-[var(--text-primary)] text-xs flex-shrink-0">{cmd}</code>
                      <span className="text-[var(--text-muted)] text-xs">{desc}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {isStaff && (
                <>
                  <SettingsCard guildId={selectedGuildId} settings={settings} onSaved={s => setSettings(prev => ({ ...prev, ...s }))} />
                  <AdjustCard guildId={selectedGuildId} leaderboard={leaderboard} emoji={emoji} onAdjusted={fetchLeaderboard} />
                </>
              )}
            </div>
          </div>
        )}
      </main>

      <AnimatePresence>
        {showAdd && (
          <AddItemModal
            guildId={selectedGuildId}
            roles={roles}
            emoji={emoji}
            onClose={() => setShowAdd(false)}
            onCreated={item => item && setItems(prev => [...prev, item])}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
